import {useMemo} from 'react'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {useProductAvailability} from './use-product-availability'

/**
 * Attributes the catalog defines per color, on the variation group. A master
 * carries its own value for these, so the group's must win even when it is
 * empty — otherwise a tile shows another color's video slots, badges or
 * tile video.
 */
const COLOR_LEVEL_ATTRIBUTES = [
    'c_color',
    'c_videoSequencePositions',
    'c_mediaProductTile',
    'c_textBadges',
    'c_badges'
]

/**
 * The custom attributes a PLP search hit exposes on `representedProduct`:
 * everything the variation group defines, with the color-level ones always
 * present (possibly undefined) so they override the master's.
 *
 * @param {object} [group] - Variation-group product fetched from SCAPI.
 * @returns {object|null}
 */
const pickRepresentedAttributes = (group) => {
    if (!group) return null
    const attributes = {}
    Object.keys(group).forEach((key) => {
        if (key.startsWith('c_') && key !== 'c_relativeUrl') attributes[key] = group[key]
    })
    COLOR_LEVEL_ATTRIBUTES.forEach((key) => {
        attributes[key] = group[key]
    })
    return attributes
}

/**
 * Reduce a master's full variants matrix to one entry per color, picking the
 * cheapest variant for each color so the tile's lowest-price logic still
 * surfaces the same number it would on PLP. Mirrors the shape that SCAPI
 * search hits expose on PLP, where `variants` carries one representative
 * variant per color rather than every color × size combination.
 *
 * @param {Array<object>} variants
 * @returns {Array<object>}
 */
const pickOneVariantPerColor = (variants) => {
    if (!Array.isArray(variants) || variants.length === 0) return variants
    const byColor = new Map()
    variants.forEach((variant) => {
        const color = variant?.variationValues?.color
        if (!color) return
        const existing = byColor.get(color)
        if (
            !existing ||
            (typeof variant?.price === 'number' &&
                typeof existing?.price === 'number' &&
                variant.price < existing.price)
        ) {
            byColor.set(color, variant)
        }
    })
    return byColor.size > 0 ? Array.from(byColor.values()) : variants
}

/**
 * Reshape an array of VickyAI / Einstein-style variationGroup products into the
 * PLP search-hit shape the storefront's `<ProductTile />` expects.
 *
 * Why this exists
 * ---------------
 * VickyAI's recommendations / similars endpoints (and Einstein's recently
 * viewed) return variationGroup-shaped objects: a single `viewType: 'large'`
 * image group, no per-color swatches, and the variants array filtered to the
 * recommended color. The product tile relies on PLP-style data — full master
 * `imageGroups` (large + swatch, tagged with `variationAttributes.color`),
 * the complete `variants` / `variationAttributes` / `variationGroups` arrays
 * — to drive color swatch chips, swap the hero image when a swatch is
 * clicked, open QuickView against the right product, and build URLs that
 * land on the recommended color's PDP.
 *
 * Strategy
 * --------
 * 1. Derive the master id by stripping each item's color code from its id
 *    (catalog convention: `<masterId><colorCode>`).
 * 2. Fetch all masters in one parallel `useProducts` call with
 *    `allImages: true` + `expand: ['images', 'variations']` (mirrors PDP).
 * 3. Fetch the variationGroups themselves — the Vicky equivalent of a PLP
 *    hit's `representedProduct`. Promotions, badges and
 *    `c_videoSequencePositions` are color-level, so they can only come from
 *    here; the master holds either nothing or another color's value.
 * 4. Build the output: master object + `representedProduct` carrying the
 *    recommended color's custom attributes, and `id` / `c_relativeUrl`
 *    overridden to Vicky's variationGroup so navigation lands on that
 *    color's PDP.
 *
 * While the master fetch is in flight, raw items are returned unchanged so
 * the UI doesn't flash skeletons.
 *
 * @param {Array<object>} rawProducts - Variation-group products from Vicky / Einstein.
 * @param {object} [options]
 * @param {boolean} [options.enabled=true] - When false, returns rawProducts as-is and skips fetch.
 * @param {boolean} [options.withFreshPricing=false] - Also merge the variation group's live price.
 * @param {boolean} [options.availabilityEnabled=true] - When false, skips the live availability fetch.
 *   Recs arrive pre-filtered to orderable products by the backend, so surfaces that
 *   don't need per-tile stock (e.g. quick-view-only carousels) can turn this off.
 * @returns {Array<object>} Master-shaped products ready for `<ProductTile />`.
 */
export const useEnrichVickyProducts = (rawProducts, options = {}) =>
    useEnrichVickyProductsWithStatus(rawProducts, options).products

/**
 * Same as `useEnrichVickyProducts`, additionally reporting whether the SFCC lookups behind
 * the result are in flight. The lookups keep the previous result while refetching, so a
 * caller that must only publish fully enriched tiles (e.g. a paginated list that keeps every
 * item it has already received) can wait for `isFetching` to clear.
 *
 * @param {Array<object>} rawProducts - Variation-group products from Vicky / Einstein.
 * @param {object} [options] - See `useEnrichVickyProducts`.
 * @returns {{products: Array<object>, isFetching: boolean}}
 */
export const useEnrichVickyProductsWithStatus = (rawProducts, options = {}) => {
    const {enabled = true, withFreshPricing = false, availabilityEnabled = true} = options
    const items = useMemo(() => (Array.isArray(rawProducts) ? rawProducts : []), [rawProducts])

    /* Strip the trailing color code from each variationGroup id to recover
     * the master id (e.g. `10009273004N` + color `04N` -> `100092730`).
     * When an item has no variationValues.color (e.g. semantic-search returns
     * bare product IDs) the id is already a master/product id — use it as-is. */
    const masterIds = useMemo(() => {
        const ids = items
            .map((item) => {
                if (!item?.id) return null
                const color = item?.variationValues?.color
                if (!color) return item.id
                if (!item.id.endsWith(color)) return null
                return item.id.slice(0, -color.length)
            })
            .filter(Boolean)
        return Array.from(new Set(ids)).join(',')
    }, [items])

    const variationGroupIds = useMemo(
        () =>
            items
                .map((item) => item?.id)
                .filter(Boolean)
                .join(','),
        [items]
    )

    const variationGroupIdArray = useMemo(
        () => items.map((item) => item?.id).filter(Boolean),
        [items]
    )

    /* Fetch the master products. `allImages: true` + the `images` expand are
     * what surface per-color swatch / large image groups; `variations` gives
     * us the full variants + variationAttributes the tile drives sizing /
     * QuickView with. `prices` / `promotions` render the tile price. Live
     * inventory comes from the Shopper Availability API below. */
    const {data: masterData, isFetching: isMasterFetching} = useProducts(
        {
            parameters: {
                ids: masterIds,
                allImages: true,
                perPricebook: true,
                expand: ['images', 'variations', 'prices', 'promotions']
            }
        },
        {
            enabled: enabled && Boolean(masterIds),
            keepPreviousData: true,
            staleTime: 5 * 60 * 1000
        }
    )

    /* The recommended colors themselves. This is the Vicky equivalent of a PLP
     * search hit's `representedProduct`: promotions, badges, tile video and
     * `c_videoSequencePositions` are all defined per color, and the master
     * carries either nothing or another color's value for them. Custom (`c_`)
     * attributes come back on the product document by default; `promotions`
     * resolves the callout for that color, `prices` feeds the optional fresh
     * pricing merge. No `select` mask: SFCC strips nested fields when one is
     * supplied and we need full shapes. */
    const {data: enrichmentData, isFetching: isEnrichmentFetching} = useProducts(
        {
            parameters: {
                ids: variationGroupIds,
                perPricebook: true,
                expand: ['prices', 'promotions']
            }
        },
        {
            enabled: enabled && Boolean(variationGroupIds),
            keepPreviousData: true
        }
    )

    /* Live inventory for the recommended colors via the Shopper Availability API.
     * Skipped when `availabilityEnabled` is false — recs are already filtered to
     * orderable by the backend, so surfaces that don't surface stock skip it. */
    const {data: availabilityMap} = useProductAvailability(variationGroupIdArray, {
        enabled: enabled && availabilityEnabled && variationGroupIdArray.length > 0
    })

    const masterById = useMemo(() => {
        const map = new Map()
        ;(masterData?.data || []).forEach((m) => {
            if (m?.id) map.set(m.id, m)
        })
        return map
    }, [masterData])

    const enrichmentById = useMemo(() => {
        const map = new Map()
        ;(enrichmentData?.data || []).forEach((p) => {
            if (p?.id) map.set(p.id, p)
        })
        return map
    }, [enrichmentData])

    const products = useMemo(() => {
        if (!enabled || items.length === 0) return items
        /* While the master fetch is still in flight we'd otherwise return
         * raw Vicky items — those have no per-color swatch image groups,
         * so the tile renders empty/text-glyph swatches that get replaced
         * a moment later when masters arrive. Returning `[]` lets callers
         * show a skeleton instead of that flicker. */
        if (masterById.size === 0) {
            return isMasterFetching && Boolean(masterIds) ? [] : items
        }

        return items.map((item) => {
            const color = item?.variationValues?.color
            const masterId =
                color && item?.id?.endsWith(color)
                    ? item.id.slice(0, -color.length)
                    : !color
                    ? item?.id || null
                    : null
            const master = masterId ? masterById.get(masterId) : null
            const group = enrichmentById.get(item?.id)
            const liveInventory = availabilityMap?.[item?.id]?.inventory
            const representedAttributes = pickRepresentedAttributes(group)

            /* Promotions are color-level. A master's `productPromotions` is the
             * union of every color's, so it renders the wrong callout on the
             * tile. Once the group resolves, its promotions replace the
             * master's outright — including when it has none, which is why
             * this isn't an `!== undefined` merge. */
            const promotionOverride = group
                ? {productPromotions: group.productPromotions}
                : item.productPromotions !== undefined
                ? {productPromotions: item.productPromotions}
                : null

            const freshOverrides = {
                ...(liveInventory ? {inventory: liveInventory} : {}),
                ...(withFreshPricing && group ? {price: group.price, currency: group.currency} : {})
            }

            // No master yet — keep Vicky's shape but still apply the color's
            // own attributes / price / inventory where available so the tile
            // shows accurate values during the brief loading window.
            if (!master) {
                return {
                    ...item,
                    ...(representedAttributes || {}),
                    ...(promotionOverride || {}),
                    ...freshOverrides
                }
            }

            const representedProduct = {
                ...(representedAttributes || {}),
                id: item.id,
                c_relativeUrl: item.c_relativeUrl || group?.c_relativeUrl,
                ...(item.variationValues || group?.variationValues
                    ? {variationValues: item.variationValues || group.variationValues}
                    : {})
            }

            return {
                ...master,
                /* The recommended color's own custom attributes — badges, tile
                 * video and `c_videoSequencePositions` — mirrored at the top
                 * level the same way a PLP hit carries them, so the master's
                 * defaults never win. */
                ...(representedAttributes || {}),
                // Keep Vicky's variationGroup id + SEO URL so the tile
                // navigates to the recommended color's PDP, not the
                // master's default. `representedProduct.id` tells the
                // tile to pre-select that color.
                id: item.id,
                c_relativeUrl: item.c_relativeUrl || master.c_relativeUrl,
                representedProduct,
                variationValues: item.variationValues || master.variationValues,
                /* PLP search hits include only ONE variant per color in
                 * `variants` — after the tile filters by color it ends up
                 * with a single entry, so `getPriceData`'s
                 * `isRange = isMaster && variants.length > 1` stays false
                 * and the price renders without a "From" prefix.
                 *
                 * `useProducts` returns the full master variants matrix
                 * (every color × size), which after the same color filter
                 * leaves N size variants → `isRange = true` → the tile
                 * shows "From £x". Slim the array down to one variant
                 * per color (preferring the cheapest, so the price
                 * shown matches the lowest-priced size). The size
                 * selector in QuickView reads `variationAttributes`,
                 * not `variants`, so this trim doesn't affect it. */
                variants: pickOneVariantPerColor(master?.variants),
                /* Master products carry no price / inventory. Fall back to
                 * whatever Vicky / Einstein returned on the raw item first,
                 * then let the live group (when `withFreshPricing`) override
                 * with the values the shopper will actually pay. */
                ...(item.price !== undefined ? {price: item.price} : {}),
                ...(item.priceMax !== undefined ? {priceMax: item.priceMax} : {}),
                ...(item.pricePerUnit !== undefined ? {pricePerUnit: item.pricePerUnit} : {}),
                ...(item.currency !== undefined ? {currency: item.currency} : {}),
                ...(item.inventory !== undefined ? {inventory: item.inventory} : {}),
                ...(promotionOverride || {}),
                ...freshOverrides
            }
        })
    }, [
        items,
        masterById,
        enrichmentById,
        availabilityMap,
        enabled,
        withFreshPricing,
        isMasterFetching,
        masterIds
    ])

    return {products, isFetching: isMasterFetching || isEnrichmentFetching}
}
